import { useState } from "react";
import { format } from "date-fns";
import {
  RouteIcon, Loader2, Info, CalendarIcon, Truck, Users, Clock, AlertTriangle,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useTransitPredictor } from "@/hooks/useTransitPredictor";
import { formatTransitDuration, formatTransitMoment, type DriverType } from "@/lib/transitEstimate";

interface Props {
  loadId: string;
  origin: string;
  destination: string;
  miles: number | null;
  /** "YYYY-MM-DD" — seeds the date picker. */
  defaultPickupDate?: string | null;
}

const TIME_OPTIONS = (() => {
  const out: { value: string; label: string }[] = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 30]) {
      const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      const d = new Date(2000, 0, 1, h, m);
      out.push({ value, label: d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }) });
    }
  }
  return out;
})();

/**
 * Shipper-side Transit Time Predictor.
 *
 * Answers "load here on this date → what day does it deliver?".
 * HOS/drive math is deterministic (src/lib/transitEstimate.ts); the AI only
 * annotates the corridor. Replaces the old feasibility widget in this slot.
 */
export function TransitTimePredictorWidget({
  loadId, origin, destination, miles, defaultPickupDate,
}: Props) {
  const [date, setDate] = useState<Date | undefined>(() => {
    if (defaultPickupDate) return new Date(`${defaultPickupDate}T12:00:00`);
    return new Date();
  });
  const [time, setTime] = useState("08:00");
  const [driverType, setDriverType] = useState<DriverType>("solo");

  const predictor = useTransitPredictor();
  const result = predictor.data;
  const hasMileage = !!miles && miles > 0;

  const run = () => {
    if (!date || !hasMileage) return;
    predictor.mutate({
      loadId,
      origin,
      destination,
      miles: miles as number,
      pickupDate: format(date, "yyyy-MM-dd"),
      pickupTime: time,
      driverType,
    });
  };

  return (
    <div className="border border-border rounded-lg bg-card p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <RouteIcon className="h-4 w-4 text-brand-teal" />
          <h3 className="font-bold text-sm text-foreground">Transit Time Predictor</h3>
          <Badge variant="outline" className="text-[10px] uppercase tracking-wide">Transit</Badge>
        </div>
      </div>

      <p className="text-[11px] text-muted-foreground">
        {origin} → {destination}
        {hasMileage && <span className="tabular-nums"> · {(miles as number).toLocaleString()} mi</span>}
      </p>

      {!hasMileage ? (
        <div className="flex items-start gap-2 text-xs text-muted-foreground">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <p>Not enough data — a transit estimate needs total lane miles.</p>
        </div>
      ) : (
        <>
          {/* Inputs */}
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn("h-8 justify-start text-left text-[11px] font-normal px-2", !date && "text-muted-foreground")}
                  >
                    <CalendarIcon className="h-3.5 w-3.5 mr-1.5 shrink-0" />
                    {date ? format(date, "MMM d, yyyy") : "Pickup date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>

              <Select value={time} onValueChange={setTime}>
                <SelectTrigger className="h-8 text-[11px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {TIME_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value} className="text-[11px]">{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant={driverType === "solo" ? "default" : "outline"}
                onClick={() => setDriverType("solo")}
                className={cn("h-8 text-[11px] gap-1.5", driverType === "solo" && "bg-brand-teal hover:bg-brand-teal/90")}
              >
                <Truck className="h-3.5 w-3.5" /> Single driver
              </Button>
              <Button
                type="button"
                variant={driverType === "team" ? "default" : "outline"}
                onClick={() => setDriverType("team")}
                className={cn("h-8 text-[11px] gap-1.5", driverType === "team" && "bg-brand-teal hover:bg-brand-teal/90")}
              >
                <Users className="h-3.5 w-3.5" /> Team
              </Button>
            </div>

            <Button
              type="button"
              onClick={run}
              disabled={predictor.isPending || !date}
              className="w-full h-8 text-[11px] bg-brand-teal hover:bg-brand-teal/90"
            >
              {predictor.isPending ? (
                <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Estimating…</>
              ) : (
                <>Estimate transit</>
              )}
            </Button>
          </div>

          {/* Result */}
          {result && (
            <div className="border-t border-border/60 pt-3 space-y-2">
              <p className="text-xs font-semibold text-foreground leading-snug">
                Loading {formatTransitMoment(new Date(
                  `${format(date as Date, "yyyy-MM-dd")}T${time}:00`,
                ))}, {driverType === "team" ? "team" : "single driver"} → delivers ~{formatTransitMoment(result.adjustedDeliveryAt)}
              </p>

              <div className="grid grid-cols-2 gap-2 text-[11px] text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <Clock className="h-3 w-3" />
                  <span className="tabular-nums">{formatTransitDuration(result.totalHours)} total transit</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Truck className="h-3 w-3" />
                  <span className="tabular-nums">
                    {result.drivingHours}h driving
                    {driverType === "solo" && ` · ${result.drivingDays} day${result.drivingDays > 1 ? "s" : ""}`}
                  </span>
                </div>
              </div>

              {driverType === "solo" && result.restBreaks > 0 && (
                <p className="text-[11px] text-muted-foreground">
                  Includes {result.restBreaks} required 10-hour reset{result.restBreaks > 1 ? "s" : ""} (11 h driving limit per day).
                </p>
              )}

              {result.delayMinutes > 0 && (
                <p className="text-[11px] text-muted-foreground">
                  {result.delayMinutes} min allowance for weather, traffic and construction on route.
                </p>
              )}

              {result.adjustmentHours !== 0 && result.adjustmentReason && (
                <p className="text-[11px] text-muted-foreground">
                  Adjusted {result.adjustmentHours > 0 ? "+" : ""}{result.adjustmentHours}h — {result.adjustmentReason}
                </p>
              )}

              {result.rolledToReceivingHours && (
                <p className="text-[11px] text-muted-foreground">
                  Arrival falls outside typical receiving hours, so it's shown at the next 7:00 AM window.
                </p>
              )}

              <p className="text-xs text-foreground leading-snug">{result.corridorNote}</p>

              {!result.conditionsAvailable && (
                <div className="flex items-start gap-1.5 text-[10px] text-muted-foreground italic">
                  <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                  <span>Conditions data unavailable — drive time and hours-of-service math only.</span>
                </div>
              )}

              <div className="flex items-start gap-1.5 text-[10px] text-muted-foreground italic border-t border-border/60 pt-2">
                <Info className="h-3 w-3 mt-0.5 shrink-0" />
                <span>Estimate only — not a commitment. Confidence {result.confidence}%.</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
